import { Drawer } from "../Drawer";
import { Icon } from "../ui/Icon";

/** Kayıt tablosu ayarları: gösterilecek kolonlar + kolon-bazlı alt-toplam (Toplam/Ortalama) + KPI kartları. */
export type ColAgg = { sum?: boolean; avg?: boolean };
export type KpiAgg = "sum" | "avg" | "last";
export type KpiCard = { id: string; title: string; fieldId: string; agg: KpiAgg };
export type FormTableSettings = { cols?: string[]; colAgg?: Record<string, ColAgg>; cards?: KpiCard[] };
export type SettingsField = { id: string; label: string; type: string; unit: string | null };

const AGG_LABEL: Record<KpiAgg, string> = { sum: "Toplam", avg: "Ortalama", last: "Son Değer" };

function uid(): string {
  try { return crypto.randomUUID(); } catch { return "kpi-" + Math.random().toString(36).slice(2, 10); }
}

export function FormTableSettingsDrawer({
  open, onClose, fields, settings, onChange,
}: {
  open: boolean;
  onClose: () => void;
  fields: SettingsField[];
  settings: FormTableSettings;
  onChange: (next: FormTableSettings) => void;
}) {
  const activeCols = settings.cols ?? fields.map((f) => f.id); // varsayılan: tüm alanlar
  const colAgg = settings.colAgg ?? {};
  const cards = settings.cards ?? [];
  const numericFields = fields.filter((f) => f.type === "number");

  const toggleCol = (id: string) => {
    const next = activeCols.includes(id) ? activeCols.filter((x) => x !== id) : [...activeCols, id];
    onChange({ ...settings, cols: next });
  };
  const setAllCols = (all: boolean) => onChange({ ...settings, cols: all ? fields.map((f) => f.id) : [] });
  const setColAgg = (id: string, patch: Partial<ColAgg>) =>
    onChange({ ...settings, colAgg: { ...colAgg, [id]: { ...colAgg[id], ...patch } } });
  const addCard = () => {
    const first = numericFields[0];
    if (!first) return;
    onChange({ ...settings, cards: [...cards, { id: uid(), title: "", fieldId: first.id, agg: "sum" }] });
  };
  const updateCard = (cid: string, patch: Partial<KpiCard>) =>
    onChange({ ...settings, cards: cards.map((c) => (c.id === cid ? { ...c, ...patch } : c)) });
  const removeCard = (cid: string) => onChange({ ...settings, cards: cards.filter((c) => c.id !== cid) });

  return (
    <Drawer open={open} onClose={onClose} size="lg" title="Tablo Ayarları" subtitle="Kolonlar · kolon alt-toplamları · KPI kartları">
      {/* ── Kolonlar ── */}
      <div className="d-card" style={{ marginBottom: 16 }}>
        <div className="d-card-head">
          <div className="left"><span className="t">Kolonlar</span><span className="s">tabloda gösterilecek alanlar + sayısal alt-toplam</span></div>
          <button type="button" className="dcard-link" onClick={() => setAllCols(activeCols.length !== fields.length)}>
            {activeCols.length === fields.length ? "Hiçbiri" : "Tümü"}
          </button>
        </div>
        <div className="d-card-body flush">
          <div className="dt2-wrap" style={{ border: 0, borderRadius: 0 }}>
            <table className="dt2">
              <thead><tr><th>Alan</th><th style={{ width: 70 }} className="c">Göster</th><th style={{ width: 90 }} className="c">Toplam (Σ)</th><th style={{ width: 100 }} className="c">Ortalama (x̄)</th></tr></thead>
              <tbody>
                {fields.map((f) => {
                  const num = f.type === "number";
                  const shown = activeCols.includes(f.id);
                  return (
                    <tr key={f.id}>
                      <td>{f.label}{f.unit ? <span className="sec" style={{ marginLeft: 4 }}>({f.unit})</span> : null}{!num ? <span className="sec" style={{ marginLeft: 6, color: "var(--neutral-400)" }}>· metin/tarih</span> : null}</td>
                      <td className="c"><input type="checkbox" checked={shown} onChange={() => toggleCol(f.id)} aria-label={`${f.label} kolonunu göster`} /></td>
                      <td className="c">{num ? <input type="checkbox" checked={!!colAgg[f.id]?.sum} onChange={(e) => setColAgg(f.id, { sum: e.target.checked })} aria-label={`${f.label} toplamı`} /> : <span style={{ color: "var(--neutral-300)" }}>—</span>}</td>
                      <td className="c">{num ? <input type="checkbox" checked={!!colAgg[f.id]?.avg} onChange={(e) => setColAgg(f.id, { avg: e.target.checked })} aria-label={`${f.label} ortalaması`} /> : <span style={{ color: "var(--neutral-300)" }}>—</span>}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* ── KPI Kartları ── */}
      <div className="d-card">
        <div className="d-card-head">
          <div className="left"><span className="t">KPI Kartları</span><span className="s">tablonun üstünde özet kart · başlık + alan + hesap</span></div>
          <button type="button" className="btn-md secondary" style={{ height: 30, padding: "0 11px", fontSize: 12 }} onClick={addCard} disabled={numericFields.length === 0} title={numericFields.length === 0 ? "Sayısal alan yok" : "Yeni KPI kartı ekle"}>
            <Icon name="plus" size={13} />Kart Ekle
          </button>
        </div>
        <div className="d-card-body" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {numericFields.length === 0 ? (
            <div className="empty-state" style={{ padding: "16px" }}><p className="ttl" style={{ fontSize: 13 }}>KPI kartı için sayısal alan yok</p><p className="sub" style={{ fontSize: 12 }}>Kart, bir sayısal alanın Toplam / Ortalama / Son Değer özetini gösterir.</p></div>
          ) : cards.length === 0 ? (
            <div className="empty-state" style={{ padding: "16px" }}><p className="ttl" style={{ fontSize: 13 }}>Henüz kart yok</p><p className="sub" style={{ fontSize: 12 }}>"Kart Ekle" ile tablonun üstünde özet kart oluşturun.</p></div>
          ) : (
            cards.map((c) => {
              const field = fields.find((f) => f.id === c.fieldId);
              return (
                <div key={c.id} style={{ display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap", padding: 10, border: "1px solid var(--neutral-200)", borderRadius: "var(--radius-md)", background: "var(--neutral-50)" }}>
                  <div className="form-field" style={{ flex: "1 1 180px", minWidth: 150, marginBottom: 0 }}>
                    <label className="lbl">Başlık</label>
                    <input className="form-input" value={c.title} placeholder={field?.label ?? "Kart başlığı"} maxLength={60} onChange={(e) => updateCard(c.id, { title: e.target.value })} />
                  </div>
                  <div className="form-field" style={{ flex: "1 1 150px", minWidth: 130, marginBottom: 0 }}>
                    <label className="lbl">Alan</label>
                    <select className="form-select" value={c.fieldId} onChange={(e) => updateCard(c.id, { fieldId: e.target.value })}>
                      {numericFields.map((f) => <option key={f.id} value={f.id}>{f.label}{f.unit ? ` (${f.unit})` : ""}</option>)}
                    </select>
                  </div>
                  <div className="form-field" style={{ flex: "0 0 140px", marginBottom: 0 }}>
                    <label className="lbl">Hesap</label>
                    <select className="form-select" value={c.agg} onChange={(e) => updateCard(c.id, { agg: e.target.value as KpiAgg })}>
                      <option value="sum">{AGG_LABEL.sum}</option>
                      <option value="avg">{AGG_LABEL.avg}</option>
                      <option value="last">{AGG_LABEL.last}</option>
                    </select>
                  </div>
                  <button type="button" className="iact danger" aria-label="Kartı sil" title="Kartı sil" style={{ marginBottom: 4 }} onClick={() => removeCard(c.id)}><Icon name="trash" size={14} /></button>
                </div>
              );
            })
          )}
        </div>
      </div>
    </Drawer>
  );
}
